// ===== Auth: Papéis, Permissões, Guards e Cadeia de Atribuição =====
import React from 'react';
import { Icon } from './icons.jsx';
import { brl } from './data.jsx';
import { useStore } from './store.jsx';

const ROLES = {
  ADMIN:       'admin',
  DIRETOR:     'diretor',
  BACKOFFICE:  'backoffice',
  BDR:         'bdr',
  SDR:         'sdr',
  CLOSER:      'closer',
  PRODUCAO:    'producao',
};

// Reserva enquanto o banco não responde -- a fonte de verdade é a tabela
// `papeis`. `tecnico_sac` saiu em 24/08 por decisão do dono do produto: o
// atendimento técnico é coberto ajustando permissões de outro cargo.
const ROLE_ORDER = ['admin', 'diretor', 'backoffice', 'financeiro', 'closer', 'sdr', 'bdr', 'producao'];

// Matriz única de autorização. Cada linha é uma ação; cada coluna, um papel.
// Regra de ouro: um papel só recebe `true` no que é indispensável para ele
// executar o próprio trabalho — nada além disso.
const PERMISSIONS = {
  'dashboard.view':            { admin:true,  diretor:true,  backoffice:true,  bdr:true,  sdr:true,  closer:true,  producao:false },
  'pipeline.view':             { admin:true,  diretor:true,  backoffice:true,  bdr:true,  sdr:true,  closer:true,  producao:false },
  // closer: true desde a 0076 -- os closers trazem negocio desde o inicio (Kaio, 25/09).
  'pipeline.create_lead':      { admin:true,  diretor:false, backoffice:false, bdr:true,  sdr:true,  closer:true,  producao:false },
  'pipeline.move_stage':       { admin:true,  diretor:false, backoffice:true,  bdr:false, sdr:true,  closer:true,  producao:false },
  'pipeline.mark_lost':        { admin:true,  diretor:false, backoffice:false, bdr:false, sdr:true,  closer:true,  producao:false },
  'pipeline.handoff':          { admin:true,  diretor:false, backoffice:false, bdr:false, sdr:true,  closer:false, producao:false },
  'pipeline.delete_lead':      { admin:true,  diretor:false, backoffice:false, bdr:true,  sdr:true,  closer:true,  producao:false, financeiro:false },
  'pipeline.view_team':        { admin:true,  diretor:true,  backoffice:false, bdr:false, sdr:false, closer:false, producao:false },
  'clientes.view':             { admin:true,  diretor:true,  backoffice:true,  bdr:true,  sdr:true,  closer:true,  producao:false },
  'clientes.create':           { admin:true,  diretor:false, backoffice:true,  bdr:true,  sdr:true,  closer:false, producao:false },
  'clientes.edit':             { admin:true,  diretor:false, backoffice:true,  bdr:false, sdr:false, closer:false, producao:false },
  'clientes.sync_erp':         { admin:true,  diretor:false, backoffice:true,  bdr:false, sdr:false, closer:false, producao:false },
  // A % do repasse do canal e dinheiro (vira o repasse em Contas a Pagar). Decisao do
  // Kaio (30/09): "o financeiro define o repasse" -- financeiro, adm_financeiro e
  // admin. O BANCO confere (migration 0085, gatilho + util.posso); isto so acompanha.
  'clientes.definir_repasse':  { admin:true,  diretor:false, backoffice:false, bdr:false, sdr:false, closer:false, producao:false, financeiro:true },
  'produtos.view':             { admin:true,  diretor:true,  backoffice:true,  bdr:true,  sdr:true,  closer:true,  producao:true  },
  'produtos.edit':             { admin:true,  diretor:true,  backoffice:true,  bdr:false, sdr:false, closer:false, producao:true, financeiro:false },
  // Mudar tabela de preco (o FATOR decide o preco das propostas) e os cadastros do catalogo
  // (familia, grupo, unidade, nicho). Decisao do Kaio (02/10): admin, diretor, financeiro e backoffice.
  // O BANCO confere (migration 0091, util.posso); isto so acompanha. Nenhuma tela escreve nisso hoje.
  'tabelas_preco.edit':        { admin:true,  diretor:true,  backoffice:true,  bdr:false, sdr:false, closer:false, producao:false, financeiro:true },
  // O CUSTO medio e a margem da empresa: admin, diretor, financeiro e ADM Financeiro; ninguem do comercial.
  // Decisao do Kaio (02/10). O BANCO confere (migration 0092, tabela `produto_custos` + util.posso); isto so
  // acompanha. Nenhuma tela mostra custo hoje; o adm_financeiro so existe no banco.
  'produtos.view_custo':       { admin:true,  diretor:true,  backoffice:false, bdr:false, sdr:false, closer:false, producao:false, financeiro:true },
  'propostas.view':            { admin:true,  diretor:true,  backoffice:true,  bdr:false, sdr:false, closer:true,  producao:false },
  'propostas.create':          { admin:true,  diretor:false, backoffice:true,  bdr:false, sdr:false, closer:true,  producao:false },
  'propostas.approve_erp':     { admin:true,  diretor:false, backoffice:true,  bdr:false, sdr:false, closer:false, producao:false },
  'tarefas.view':              { admin:true,  diretor:true,  backoffice:true,  bdr:true,  sdr:true,  closer:true,  producao:true  },
  'tarefas.create':            { admin:true,  diretor:true,  backoffice:true,  bdr:true,  sdr:true,  closer:true,  producao:true  },
  'tarefas.view_team':         { admin:true,  diretor:true,  backoffice:false, bdr:false, sdr:false, closer:false, producao:false },
  'sac.view':                  { admin:true,  diretor:true,  backoffice:true,  bdr:false, sdr:false, closer:true,  producao:true  },
  'sac.create':                { admin:true,  diretor:false, backoffice:true,  bdr:false, sdr:false, closer:true,  producao:false },
  'sac.resolve':               { admin:true,  diretor:false, backoffice:false, bdr:false, sdr:false, closer:false, producao:false },
  'posvenda.view':             { admin:true,  diretor:true,  backoffice:true,  bdr:false, sdr:false, closer:true,  producao:true  },
  'posvenda.edit':             { admin:true,  diretor:false, backoffice:true,  bdr:false, sdr:false, closer:false, producao:true  },
  'gestao.view':               { admin:true,  diretor:true,  backoffice:false, bdr:false, sdr:false, closer:false, producao:false },
  'gestao.edit_metas':         { admin:true,  diretor:false, backoffice:false, bdr:false, sdr:false, closer:false, producao:false },
  'relatorios.view':           { admin:true,  diretor:true,  backoffice:false, bdr:false, sdr:false, closer:false, producao:false },
  'relatorios.view_comissoes': { admin:true,  diretor:true,  backoffice:false, bdr:true,  sdr:true,  closer:true,  producao:false },
  'financeiro.view_valores':   { admin:true,  diretor:true,  backoffice:true,  bdr:true,  sdr:true,  closer:true,  producao:false },
  'admin.view':                { admin:true,  diretor:false, backoffice:false, bdr:false, sdr:false, closer:false, producao:false },
  'admin.manage_users':        { admin:true,  diretor:false, backoffice:false, bdr:false, sdr:false, closer:false, producao:false },
  'ajustes.view':              { admin:true,  diretor:true,  backoffice:true,  bdr:false, sdr:false, closer:false, producao:false },
  'ajustes.edit':              { admin:true,  diretor:true,  backoffice:true,  bdr:false, sdr:false, closer:false, producao:false },
  'financeiro.view':           { admin:true,  diretor:true,  backoffice:false, bdr:false, sdr:false, closer:false, producao:false },
  'financeiro.definir_regras': { admin:true,  diretor:false, backoffice:false, bdr:false, sdr:false, closer:false, producao:false },
  'financeiro.aprovar':        { admin:true,  diretor:true,  backoffice:false, bdr:false, sdr:false, closer:false, producao:false },
};

// Papel Financeiro: define regras de comissionamento, repasses e descontos, e aprova o que é pago.
// Recebe só o que precisa para isso — nenhum acesso de escrita ao comercial.
['dashboard.view','clientes.view','produtos.view','tarefas.view','tarefas.create','propostas.view',
 'relatorios.view','relatorios.view_comissoes','financeiro.view_valores','financeiro.view',
 'financeiro.definir_regras','financeiro.aprovar','gestao.view','ajustes.view'
].forEach(p => { PERMISSIONS[p] = { ...(PERMISSIONS[p] || {}), financeiro: true }; });

// Rótulo legível de cada ação (usado na Matriz de permissões do Admin).
const PERM_GROUPS = [
  { id: 'geral',      label: 'Geral',            perms: ['dashboard.view', 'produtos.view', 'produtos.edit', 'tabelas_preco.edit', 'tarefas.view', 'tarefas.create', 'tarefas.view_team'] },
  { id: 'comercial',  label: 'Comercial',        perms: ['pipeline.view', 'pipeline.create_lead', 'pipeline.move_stage', 'pipeline.mark_lost', 'pipeline.handoff', 'pipeline.delete_lead', 'pipeline.view_team', 'propostas.view', 'propostas.create', 'propostas.approve_erp'] },
  { id: 'carteira',   label: 'Carteira',         perms: ['clientes.view', 'clientes.create', 'clientes.edit', 'clientes.sync_erp', 'clientes.definir_repasse'] },
  { id: 'operacao',   label: 'Operação',         perms: ['posvenda.view', 'posvenda.edit', 'sac.view', 'sac.create', 'sac.resolve'] },
  { id: 'financeiro', label: 'Gestão & números', perms: ['financeiro.view_valores', 'produtos.view_custo', 'financeiro.view', 'financeiro.definir_regras', 'financeiro.aprovar', 'gestao.view', 'gestao.edit_metas', 'relatorios.view', 'relatorios.view_comissoes'] },
  { id: 'admin',      label: 'Administração',    perms: ['admin.view', 'admin.manage_users', 'ajustes.view', 'ajustes.edit'] },
];

const PERM_LABELS = {
  'dashboard.view': 'Ver o Resumo comercial',
  'produtos.view': 'Consultar catálogo e estoque',
  'produtos.edit': 'Editar descrição técnica, dimensões e peso',
  'tabelas_preco.edit': 'Editar tabelas de preço e cadastros do catálogo',
  'produtos.view_custo': 'Ver o custo médio dos produtos',
  'tarefas.view': 'Ver as próprias tarefas',
  'tarefas.create': 'Criar tarefas',
  'tarefas.view_team': 'Ver tarefas da equipe',
  'pipeline.view': 'Abrir o Pipeline',
  'pipeline.create_lead': 'Criar negócio',
  'pipeline.move_stage': 'Mover etapa',
  'pipeline.mark_lost': 'Marcar perda',
  'pipeline.handoff': 'Repassar para Closer',
  'pipeline.delete_lead': 'Excluir lead',
  'pipeline.view_team': 'Ver pipeline da equipe',
  'propostas.view': 'Ver propostas',
  'propostas.create': 'Montar proposta',
  'propostas.approve_erp': 'Aprovar pedido no ERP',
  'clientes.view': 'Ver clientes',
  'clientes.create': 'Cadastrar cliente',
  'clientes.edit': 'Editar cadastro',
  'clientes.sync_erp': 'Sincronizar com o Nomus',
  'clientes.definir_repasse': 'Definir a % de repasse do canal',
  'posvenda.view': 'Ver jornada de pós-venda',
  'posvenda.edit': 'Avançar etapa de pós-venda',
  'sac.view': 'Ver chamados',
  'sac.create': 'Abrir chamado',
  'sac.resolve': 'Resolver chamado',
  'financeiro.view_valores': 'Ver valores em R$',
  'financeiro.view': 'Área Financeiro (comissões e repasses)',
  'financeiro.definir_regras': 'Definir regras de comissionamento',
  'financeiro.aprovar': 'Aprovar e liquidar comissões',
  'gestao.view': 'Área de Gestão',
  'gestao.edit_metas': 'Definir metas',
  'relatorios.view': 'Relatórios da empresa',
  'relatorios.view_comissoes': 'Ver a própria comissão',
  'admin.view': 'Área Admin',
  'admin.manage_users': 'Gerenciar usuários',
  'ajustes.view': 'Ver Ajustes (preços, transportadoras)',
  'ajustes.edit': 'Alterar parâmetros comerciais',
};

// Escopo de dados por domínio — o que o login enxerga dentro do que já pode abrir.
//   team   = todos os registros · own = apenas os seus
//   ganhos = somente negócios fechados (operação, sem acesso comercial)
//   none   = nenhum registro
const DATA_SCOPE = {
  admin:       { leads: 'team',   clientes: 'team', tarefas: 'team', sac: 'team' },
  diretor:     { leads: 'team',   clientes: 'team', tarefas: 'team', sac: 'team' },
  backoffice:  { leads: 'team',   clientes: 'team', tarefas: 'own',  sac: 'team' },
  bdr:         { leads: 'own',    clientes: 'own',  tarefas: 'own',  sac: 'none' },
  sdr:         { leads: 'own',    clientes: 'own',  tarefas: 'own',  sac: 'none' },
  closer:      { leads: 'own',    clientes: 'own',  tarefas: 'own',  sac: 'own'  },
  producao:    { leads: 'ganhos', clientes: 'none', tarefas: 'own',  sac: 'team' },
  financeiro:  { leads: 'team',   clientes: 'team', tarefas: 'own',  sac: 'team' },
};

// Compatibilidade: escopo "principal" (carteira/negócios) de cada papel.
const ROLE_SCOPE = {
  admin:       'team',
  diretor:     'team',
  backoffice:  'team',
  bdr:         'own',
  sdr:         'own',
  closer:      'own',
  producao:    'own',
  financeiro:  'team',
};

const ROLE_LABELS = {
  admin:       'Admin',
  diretor:     'Diretor',
  backoffice:  'Backoffice',
  bdr:         'BDR',
  sdr:         'SDR',
  closer:      'Closer',
  producao:    'Produção',
  financeiro:  'Financeiro',
};

const ROLE_DESC = {
  admin:       'Acesso total · configura o CRM',
  diretor:     'Leitura da operação inteira · metas e relatórios',
  backoffice:  'Cadastro, propostas e emissão de pedido no ERP',
  bdr:         'Prospecção · cria e trabalha os próprios leads',
  sdr:         'Qualificação · repassa negócios ao Closer',
  closer:      'Fechamento · apenas a própria carteira',
  producao:    'Chão de fábrica · o que produzir e expedir, sem dado comercial',
  financeiro:  'Comissões, repasses e descontos · define as regras e aprova o pagamento',
};

// Rota inicial de cada papel — o login cai direto no que ele usa.
const ROLE_HOME = {
  producao:    '/pos-venda',
};

// ===== Registro editável de cargos (RBAC) =====
// A matriz acima é o padrão de fábrica. O Admin pode alterar qualquer célula,
// criar cargos novos e ajustar escopo — as mudanças mutam os mesmos objetos
// (PERMISSIONS, DATA_SCOPE, ROLE_*) para que todo o CRM leia sempre a versão
// vigente, e ficam gravadas no navegador.

// Dependências entre ações: conceder a chave sem conceder a porta é inconsistência.
const PERM_DEPS = {
  'pipeline.create_lead': 'pipeline.view',
  'pipeline.move_stage': 'pipeline.view',
  'pipeline.mark_lost': 'pipeline.view',
  'pipeline.handoff': 'pipeline.view',
  'pipeline.delete_lead': 'pipeline.view',
  'pipeline.view_team': 'pipeline.view',
  'clientes.create': 'clientes.view',
  'clientes.edit': 'clientes.view',
  'clientes.sync_erp': 'clientes.edit',
  'clientes.definir_repasse': 'clientes.view',
  'produtos.edit': 'produtos.view',
  'tabelas_preco.edit': 'produtos.view',
  'produtos.view_custo': 'produtos.view',
  'propostas.create': 'propostas.view',
  'propostas.approve_erp': 'propostas.view',
  'tarefas.create': 'tarefas.view',
  'tarefas.view_team': 'tarefas.view',
  'sac.create': 'sac.view',
  'sac.resolve': 'sac.view',
  'posvenda.edit': 'posvenda.view',
  'gestao.edit_metas': 'gestao.view',
  'financeiro.definir_regras': 'financeiro.view',
  'financeiro.aprovar': 'financeiro.view',
  'admin.manage_users': 'admin.view',
  'ajustes.edit': 'ajustes.view',
};

// Células que não podem ser desmarcadas: sem elas ninguém reabre esta tela.
const PERM_LOCKED = { admin: ['admin.view', 'admin.manage_users'] };
const SCOPE_DOMINIOS = [
  { key: 'leads',    label: 'Negócios' },
  { key: 'clientes', label: 'Clientes' },
  { key: 'tarefas',  label: 'Tarefas'  },
  { key: 'sac',      label: 'Chamados' },
];
const SCOPE_OPCOES = [
  { value: 'team',   label: 'Toda a empresa' },
  { value: 'own',    label: 'Somente os seus' },
  { value: 'ganhos', label: 'Só negócios fechados' },
  { value: 'none',   label: 'Nenhum' },
];

const RBAC_KEY = 'beamcrm.rbac.v1';
const RBAC_BUILTIN = ROLE_ORDER.slice();
const RBAC_CUSTOM = new Set();
const rbacClone = (o) => JSON.parse(JSON.stringify(o));
const RBAC_DEFAULTS = rbacClone({
  perms: PERMISSIONS, scope: DATA_SCOPE, rscope: ROLE_SCOPE,
  labels: ROLE_LABELS, descs: ROLE_DESC, order: ROLE_ORDER, home: ROLE_HOME,
});

function rbacSlug(s) {
  return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 28);
}

function rbacReplace(alvo, novo) {
  Object.keys(alvo).forEach(k => { delete alvo[k]; });
  Object.assign(alvo, rbacClone(novo));
}

function rbacApply(snap) {
  // Ações novas do código sempre entram, mesmo que o snapshot salvo não as conheça.
  const perms = rbacClone(RBAC_DEFAULTS.perms);
  Object.entries(snap.perms || {}).forEach(([p, linha]) => {
    if (perms[p]) Object.assign(perms[p], linha); else perms[p] = { ...linha };
  });
  rbacReplace(PERMISSIONS, perms);
  rbacReplace(DATA_SCOPE, snap.scope || RBAC_DEFAULTS.scope);
  rbacReplace(ROLE_SCOPE, snap.rscope || RBAC_DEFAULTS.rscope);
  rbacReplace(ROLE_LABELS, snap.labels || RBAC_DEFAULTS.labels);
  rbacReplace(ROLE_DESC, snap.descs || RBAC_DEFAULTS.descs);
  rbacReplace(ROLE_HOME, snap.home || RBAC_DEFAULTS.home);
  const ordem = (snap.order && snap.order.length ? snap.order : RBAC_DEFAULTS.order).filter(r => ROLE_LABELS[r]);
  ROLE_ORDER.length = 0; ordem.forEach(r => ROLE_ORDER.push(r));
  RBAC_CUSTOM.clear(); (snap.custom || []).forEach(r => { if (!RBAC_BUILTIN.includes(r)) RBAC_CUSTOM.add(r); });
}

// ===== Edicao de cargos contra o banco (Parte 2, migration 0080) =====
// Funcoes puras: recebem as permissoes ATUAIS de um cargo ({acao: bool}) e
// devolvem as novas, com a mesma coerencia de PERM_DEPS que setPerm aplicava na
// memoria. A tela grava so a diferenca, por admin_salvar_cargo.

// admin.* so no cargo Admin: a 0080 recusa no banco, e a matriz nem oferece
// (Kaio, 28/09: "so o admin administra cargos e usuarios").
function soDoAdmin(perm, role) {
  return role !== 'admin' && String(perm).startsWith('admin.');
}

function travadaPara(perm, role) {
  return (PERM_LOCKED[role] || []).includes(perm) || soDoAdmin(perm, role);
}

function concederComDependencias(novas, perm, role) {
  const vistos = new Set();
  for (let a = perm; a && !vistos.has(a); a = PERM_DEPS[a]) {
    vistos.add(a);
    if (!soDoAdmin(a, role)) novas[a] = true;
  }
}

// Revogar a leitura derruba o que dependia dela, em cascata.
function derrubarOrfas(novas, role) {
  let mudou = true;
  while (mudou) {
    mudou = false;
    for (const [p, requer] of Object.entries(PERM_DEPS)) {
      if ((PERM_LOCKED[role] || []).includes(p)) continue;
      if (novas[p] && !novas[requer]) { novas[p] = false; mudou = true; }
    }
  }
}

function permissoesAoAlternar(atuais, perm, role, on) {
  const novas = { ...atuais };
  if (travadaPara(perm, role)) return novas;
  if (on) concederComDependencias(novas, perm, role);
  else { novas[perm] = false; derrubarOrfas(novas, role); }
  return novas;
}

function permissoesDoGrupo(atuais, perms, role, on) {
  const novas = { ...atuais };
  for (const p of perms || []) {
    if (travadaPara(p, role)) continue;
    if (on) concederComDependencias(novas, p, role);
    else novas[p] = false;
  }
  if (!on) derrubarOrfas(novas, role);
  return novas;
}

function diferencaDePermissoes(antes, depois) {
  const d = {};
  for (const [acao, v] of Object.entries(depois)) if (!!(antes || {})[acao] !== !!v) d[acao] = !!v;
  return d;
}

// Padrao de fabrica de um cargo, vindo da reserva do codigo -- ou null quando a
// reserva nao cobre TODAS as acoes dele. Medido em 29/09: o Financeiro aparece
// em 2 das 36 linhas de PERMISSIONS (o banco tem 15 linhas para ele, 14
// liberadas); "restaurar" a partir do codigo o deixaria quase sem acesso.
function valoresDeFabrica(codigo) {
  if (!RBAC_DEFAULTS.labels[codigo] || !RBAC_DEFAULTS.scope[codigo]) return null;
  const linhas = Object.values(RBAC_DEFAULTS.perms);
  if (!linhas.every((porPapel) => codigo in porPapel)) return null;
  const permissoes = {};
  for (const [acao, porPapel] of Object.entries(RBAC_DEFAULTS.perms)) permissoes[acao] = !!porPapel[codigo];
  return {
    label: RBAC_DEFAULTS.labels[codigo],
    descricao: RBAC_DEFAULTS.descs[codigo] || '',
    escopos: { ...RBAC_DEFAULTS.scope[codigo] },
    permissoes,
  };
}

function permissoesDoCargo(role) {
  return Object.fromEntries(Object.keys(PERMISSIONS).map((acao) => [acao, !!(PERMISSIONS[acao] || {})[role]]));
}

const RBAC = {
  version: 0,
  _subs: new Set(),
  subscribe(fn) { RBAC._subs.add(fn); return () => RBAC._subs.delete(fn); },
  getVersion() { return RBAC.version; },
  isCustom(role) { return RBAC_CUSTOM.has(role); },
  isLocked(perm, role) { return (PERM_LOCKED[role] || []).includes(perm); },
  emit() {
    RBAC.version++;
    // Tarefa 12: parou de escrever em beamcrm.rbac.v1 — era persistência
    // forjável (localStorage) da mesma família da sessão fake que este
    // login.jsx substituiu. As mutações continuam valendo em memória (por
    // isso os subscribers ainda são notificados); só deixam de sobreviver
    // a um reload. load() abaixo continua lendo o que já estiver salvo de
    // sessões anteriores, só não grava nada novo.
    RBAC._subs.forEach(f => f());
  },
  load() {
    try {
      const raw = localStorage.getItem(RBAC_KEY);
      if (raw) rbacApply(JSON.parse(raw));
    } catch (e) {}
  },

  // Liga/desliga uma ação para um cargo, mantendo a coerência com PERM_DEPS:
  // conceder puxa as leituras necessárias; revogar derruba o que dependia dela.
  setPerm(perm, role, on) {
    if (RBAC.isLocked(perm, role)) return { ok: false, erro: 'Esta permissão é obrigatória para o Admin.' };
    if (!PERMISSIONS[perm]) PERMISSIONS[perm] = {};
    if (on) {
      const vistos = new Set();
      let atual = perm;
      while (atual && !vistos.has(atual)) {
        vistos.add(atual);
        if (!PERMISSIONS[atual]) PERMISSIONS[atual] = {};
        PERMISSIONS[atual][role] = true;
        atual = PERM_DEPS[atual];
      }
    } else {
      PERMISSIONS[perm][role] = false;
      let mudou = true;
      while (mudou) {
        mudou = false;
        Object.entries(PERM_DEPS).forEach(([p, requer]) => {
          if (RBAC.isLocked(p, role)) return;
          if (PERMISSIONS[p] && PERMISSIONS[p][role] && !(PERMISSIONS[requer] && PERMISSIONS[requer][role])) {
            PERMISSIONS[p][role] = false; mudou = true;
          }
        });
      }
    }
    RBAC.emit();
    return { ok: true };
  },

  setGroup(perms, role, on) {
    (perms || []).forEach(p => {
      if (RBAC.isLocked(p, role)) return;
      if (!PERMISSIONS[p]) PERMISSIONS[p] = {};
      if (on) {
        const vistos = new Set(); let atual = p;
        while (atual && !vistos.has(atual)) { vistos.add(atual); if (!PERMISSIONS[atual]) PERMISSIONS[atual] = {}; PERMISSIONS[atual][role] = true; atual = PERM_DEPS[atual]; }
      } else PERMISSIONS[p][role] = false;
    });
    if (!on) {
      let mudou = true;
      while (mudou) {
        mudou = false;
        Object.entries(PERM_DEPS).forEach(([p, requer]) => {
          if (RBAC.isLocked(p, role)) return;
          if (PERMISSIONS[p] && PERMISSIONS[p][role] && !(PERMISSIONS[requer] && PERMISSIONS[requer][role])) { PERMISSIONS[p][role] = false; mudou = true; }
        });
      }
    }
    RBAC.emit();
  },

  createRole({ id, label, desc, base, scopes, home }) {
    const nome = String(label || '').trim();
    if (!nome) return { ok: false, erro: 'Dê um nome ao cargo.' };
    const key = rbacSlug(id || nome);
    if (!key) return { ok: false, erro: 'Chave inválida.' };
    if (ROLE_LABELS[key]) return { ok: false, erro: `Já existe um cargo com a chave "${key}".` };
    Object.keys(PERMISSIONS).forEach(p => { PERMISSIONS[p][key] = base ? !!PERMISSIONS[p][base] : false; });
    ROLE_LABELS[key] = nome;
    ROLE_DESC[key] = String(desc || '').trim();
    DATA_SCOPE[key] = { leads: 'own', clientes: 'own', tarefas: 'own', sac: 'none', ...(base ? DATA_SCOPE[base] : null), ...(scopes || {}) };
    ROLE_SCOPE[key] = DATA_SCOPE[key].clientes === 'team' ? 'team' : 'own';
    if (home) ROLE_HOME[key] = home;
    ROLE_ORDER.push(key);
    RBAC_CUSTOM.add(key);
    RBAC.emit();
    return { ok: true, id: key };
  },

  updateRole(key, { label, desc, scopes, home }) {
    if (!ROLE_LABELS[key]) return { ok: false, erro: 'Cargo inexistente.' };
    if (label != null && String(label).trim()) ROLE_LABELS[key] = String(label).trim();
    if (desc != null) ROLE_DESC[key] = String(desc).trim();
    if (scopes) {
      DATA_SCOPE[key] = { ...(DATA_SCOPE[key] || {}), ...scopes };
      ROLE_SCOPE[key] = DATA_SCOPE[key].clientes === 'team' ? 'team' : 'own';
    }
    if (home !== undefined) { if (home) ROLE_HOME[key] = home; else delete ROLE_HOME[key]; }
    RBAC.emit();
    return { ok: true };
  },

  deleteRole(key) {
    if (!RBAC_CUSTOM.has(key)) return { ok: false, erro: 'Cargos de fábrica não podem ser excluídos.' };
    Object.keys(PERMISSIONS).forEach(p => { delete PERMISSIONS[p][key]; });
    delete ROLE_LABELS[key]; delete ROLE_DESC[key]; delete DATA_SCOPE[key];
    delete ROLE_SCOPE[key]; delete ROLE_HOME[key];
    const i = ROLE_ORDER.indexOf(key); if (i >= 0) ROLE_ORDER.splice(i, 1);
    RBAC_CUSTOM.delete(key);
    RBAC.emit();
    return { ok: true };
  },

  resetRole(key) {
    if (!RBAC_DEFAULTS.labels[key]) return { ok: false, erro: 'Cargo criado aqui não tem padrão de fábrica.' };
    Object.keys(PERMISSIONS).forEach(p => {
      const linha = RBAC_DEFAULTS.perms[p];
      PERMISSIONS[p][key] = !!(linha && linha[key]);
    });
    DATA_SCOPE[key] = rbacClone(RBAC_DEFAULTS.scope[key] || {});
    ROLE_SCOPE[key] = RBAC_DEFAULTS.rscope[key] || 'own';
    ROLE_LABELS[key] = RBAC_DEFAULTS.labels[key];
    ROLE_DESC[key] = RBAC_DEFAULTS.descs[key] || '';
    RBAC.emit();
    return { ok: true };
  },

  resetAll() {
    rbacApply({ ...rbacClone(RBAC_DEFAULTS), custom: [] });
    RBAC.emit();
    return { ok: true };
  },

  // O banco manda (Parte 2): quando `papeis` chega, as constantes passam a
  // espelha-lo, e todo leitor delas -- scopeLeads/scopeClientes, a matriz, os
  // rotulos -- ve o mesmo que a RLS aplica. Medido em 29/09: os cargos da 0076
  // (adm_vendas, orcamentista, adm_financeiro) nao existiam em DATA_SCOPE,
  // scopeOf devolvia 'none' e o pipeline deles abria VAZIO, com o banco
  // liberando. ROLE_HOME fica de fora de proposito: `papeis.home` difere da
  // rota de entrada de hoje, e mudar onde cada um cai no login nao foi pedido.
  sincronizarComBanco(papeis) {
    const codigos = Object.keys(papeis || {});
    if (!codigos.length) return;
    codigos.sort((a, b) => (papeis[a].ordem - papeis[b].ordem) || a.localeCompare(b));
    const acoes = new Set(Object.keys(RBAC_DEFAULTS.perms));
    codigos.forEach((c) => Object.keys(papeis[c].permissoes || {}).forEach((a) => acoes.add(a)));
    const perms = {};
    acoes.forEach((a) => {
      perms[a] = {};
      codigos.forEach((c) => { perms[a][c] = !!(papeis[c].permissoes || {})[a]; });
    });
    const escopos = {};
    codigos.forEach((c) => {
      escopos[c] = { leads: 'none', clientes: 'none', tarefas: 'none', sac: 'none', ...(papeis[c].escopos || {}) };
    });
    rbacReplace(PERMISSIONS, perms);
    rbacReplace(DATA_SCOPE, escopos);
    rbacReplace(ROLE_SCOPE, Object.fromEntries(codigos.map((c) => [c, escopos[c].clientes === 'team' ? 'team' : 'own'])));
    rbacReplace(ROLE_LABELS, Object.fromEntries(codigos.map((c) => [c, papeis[c].label])));
    rbacReplace(ROLE_DESC, Object.fromEntries(codigos.map((c) => [c, papeis[c].descricao || ''])));
    ROLE_ORDER.length = 0; codigos.forEach((c) => ROLE_ORDER.push(c));
    RBAC_CUSTOM.clear(); codigos.forEach((c) => { if (!papeis[c].builtin) RBAC_CUSTOM.add(c); });
    RBAC.emit();
  },

  // Ações concedidas sem a leitura que elas exigem.
  auditar() {
    const falhas = [];
    ROLE_ORDER.forEach(papel => {
      Object.entries(PERM_DEPS).forEach(([perm, requer]) => {
        const tem = PERMISSIONS[perm] && PERMISSIONS[perm][papel];
        const base = PERMISSIONS[requer] && PERMISSIONS[requer][papel];
        if (tem && !base) falhas.push({ papel, perm, requer });
      });
    });
    return falhas;
  },
};

RBAC.load();

function useRbacVersion() {
  return React.useSyncExternalStore(RBAC.subscribe, RBAC.getVersion, RBAC.getVersion);
}

// ===== Hooks de permissão =====

function usePermission(action) {
  const { me, papeis } = useStore();
  const papel = (me?.papel ?? 'closer').toLowerCase();
  // Enquanto o banco não respondeu (`papeis` vazio), cai nas constantes. Isso
  // evita que a tela negue tudo no primeiro instante e pisque o conteúdo.
  // NÃO é fonte permanente: quando `papeis` chega, ele manda.
  const doBanco = papeis && papeis[papel];
  if (doBanco) return !!doBanco.permissoes[action];
  return PERMISSIONS[action]?.[papel] ?? false;
}

function usePermissions(...actions) {
  const { me, papeis } = useStore();
  const papel = (me?.papel ?? 'closer').toLowerCase();
  const doBanco = papeis && papeis[papel];
  return actions.reduce((acc, action) => {
    acc[action] = doBanco ? !!doBanco.permissoes[action] : (PERMISSIONS[action]?.[papel] ?? false);
    return acc;
  }, {});
}

function useScope() {
  const { me, papeis } = useStore();
  const papel = (me?.papel ?? 'closer').toLowerCase();
  const doBanco = papeis && papeis[papel];
  if (doBanco) return doBanco.escopos.leads ?? 'own';
  return ROLE_SCOPE[papel] ?? 'own';
}

// A POSSE do lead (decisão do Kaio, 01/10). Quem trouxe um lead e o repassou ainda o VÊ, mas já não é o
// dono. O banco só deixa escrever o dono atual (ou o escopo `team`), então a tela só oferece as ações de
// escrita (avançar, perder, reabrir, repassar, editar, excluir) a eles. Devolve uma função porque o dono
// muda de lead para lead.
function useEscreveNoLead() {
  const { me } = useStore();
  const escopo = useScope();
  return (lead) => escopo === 'team' || (!!lead && !!me && lead.ownerId === me.id);
}

function filterByScope(items, me, scope) {
  if (scope === 'team') return items;
  return items.filter(l => l.ownerId === me.id);
}

// Lista de cargos para as telas montarem seletores e colunas.
//
// A reserva nas constantes não é decoração: enquanto `listarPapeis()` não
// respondeu -- e se ela falhar -- a tela de usuários ficaria com o seletor de
// cargo VAZIO, e quem estivesse editando alguém perderia o cargo ao salvar.
// Com a reserva, o pior caso é a tela mostrar os cargos de fábrica.
function useCargos() {
  const store = useStore();
  const doBanco = store?.papeis;

  return React.useMemo(() => {
    const entradas = Object.entries(doBanco || {});
    if (entradas.length) {
      return entradas
        .map(([codigo, p]) => ({ codigo, label: p.label, descricao: p.descricao }))
        .sort((a, b) => {
          const oa = doBanco[a.codigo].ordem, ob = doBanco[b.codigo].ordem;
          return oa - ob || a.codigo.localeCompare(b.codigo);
        });
    }
    return ROLE_ORDER.map(codigo => ({
      codigo,
      label: ROLE_LABELS[codigo],
      descricao: (ROLE_DESC || {})[codigo] || '',
    }));
  }, [doBanco]);
}

// ===== Escopo de dados =====
//
// CONVENIÊNCIA DE INTERFACE, NÃO SEGURANÇA.
// O isolamento de verdade está nas policies de RLS do banco (migration
// db/migrations/0022_rls_escopos_reais.sql / supabase/migrations/
// 20260810120022_rls_escopos_reais.sql), que portam a mesma regra de
// scopeLeads/scopeClientes/scopeAlerts/scopeChamados para dentro do
// Postgres. As quatro funções abaixo só evitam pedir ao servidor o que
// ele não devolveria — nunca são a barreira de fato: um vendedor que
// falar HTTP direto com o PostgREST, pulando esta tela, cai na mesma
// RLS. Provado por tests/unit/rls.test.js (fala com o PostgREST local,
// sem abrir navegador nenhum).

function scopeOf(papel, dominio) {
  const p = (papel || 'closer').toLowerCase();
  return (DATA_SCOPE[p] && DATA_SCOPE[p][dominio]) || 'none';
}

function scopeLeads(leads, me) {
  const s = scopeOf(me?.papel, 'leads');
  if (s === 'team') return leads;
  if (s === 'none') return [];
  if (s === 'ganhos') return leads.filter(l => l.stage === 'GANHO' && !l.perdido);
  return leads.filter(l => l.ownerId === me.id || (l.originador && l.originador.id === me.id));
}

function scopeClientes(clientes, me) {
  const s = scopeOf(me?.papel, 'clientes');
  if (s === 'team') return clientes;
  if (s === 'none') return [];
  // "Só os seus": no banco, a regra `clientes_sel` (`dono_do_cliente`) já
  // entrega só os clientes que têm um negócio DO usuário -- então o que chegou
  // aqui já está recortado. Esta função só tira o que a própria carga declara
  // como de OUTRO dono.
  //
  // Exigir `c.ownerId === me.id` esvaziava a lista de closer, SDR e BDR: a
  // tabela `clientes` não tem dono, e `mapearCliente` fixa `ownerId: null` em
  // tudo que vem do banco. Achado de 30/09 no staging: o closer era dono de um
  // negócio, a API devolvia o cliente e a tela dizia "0 clientes".
  return clientes.filter(c => c.ownerId == null || c.ownerId === me.id);
}

function scopeAlerts(alerts, me) {
  const s = scopeOf(me?.papel, 'tarefas');
  if (s === 'team') return alerts;
  if (s === 'none') return [];
  return alerts.filter(a => a.autor === me.id || (a.envolvidos || []).includes(me.id));
}

function scopeChamados(chamados, me, clientesVisiveis) {
  const s = scopeOf(me?.papel, 'sac');
  if (s === 'team') return chamados;
  if (s === 'none') return [];
  const ids = new Set((clientesVisiveis || []).map(c => c.id));
  return chamados.filter(c => c.responsavelId === me.id || ids.has(c.clienteId));
}

// ===== Valores monetários =====
// Papéis operacionais (Produção, Técnico SAC) não enxergam R$ em lugar nenhum.
function useMoney() {
  const pode = usePermission('financeiro.view_valores');
  return React.useCallback((v) => (pode ? brl(v) : '—'), [pode]);
}

// ===== Guard component =====

function RequirePermission({ action, fallback, children }) {
  const allowed = usePermission(action);
  if (allowed) return children;
  return fallback || null;
}

// ===== Config visual da Cadeia de Atribuição =====

const CADEIA_TIPO_CONFIG = {
  geracao:           { label: 'Lead gerado por IA',  icon: 'cpu',            color: 'accent'  },
  primeiro_contato:  { label: 'Primeiro contato',    icon: 'phone',          color: 'cool'    },
  qualificacao:      { label: 'Qualificado',         icon: 'check-circle-2', color: 'ok'      },
  reuniao_realizada: { label: 'Reunião realizada',   icon: 'video',          color: 'warn'    },
  handoff:           { label: 'Handoff para Closer', icon: 'arrow-right',    color: 'corten'  },
  // Quem CADASTROU o negocio pelo closer (ADM de vendas, orcamentista) -- sem
  // tomar o lugar de quem trouxe (Kaio, 25/09).
  cadastro:          { label: 'Cadastrado por',      icon: 'user-plus',      color: 'cool'    },
  fechamento:        { label: 'Negócio fechado',     icon: 'trophy',         color: 'ok'      },
};

// ===== Resumo de atribuição (quem participou de cada marco) =====
// Mostra os três papéis do rastreio de conversão. Um lead pode nascer sem
// agente, ser fechado sem passar por SDR, ou ter a mesma pessoa em tudo —
// cada ausência é explicitada em vez de herdar o ator errado.
function AtribuicaoResumo({ lead }) {
  const store = useStore();
  const at = store.getAtribuicao(lead);
  if (!at) return null;
  const papeis = [
    { key: 'orig',  label: 'Originou',    icon: 'sparkles',       pessoa: at.originador,   ausente: 'Sem originador registrado' },
    { key: 'qual',  label: 'Qualificou',  icon: 'check-circle-2', pessoa: at.qualificador, ausente: 'Ainda não qualificado', nota: at.qualificacaoImplicita ? 'implícito ao avançar etapas' : null },
    { key: 'close', label: at.closer && at.closer.previsto ? 'Responsável' : 'Fechou', icon: at.closer && at.closer.previsto ? 'user' : 'trophy', pessoa: at.closer, pendente: !!(at.closer && at.closer.previsto), ausente: 'Ainda não fechado' },
  ];
  return (
    <div className="atrib-resumo">
      <div className="ar-head">
        <Icon name="git-merge" size={13} />
        Rastreio de conversão
        {at.soloCompleto && at.fechado && <span className="ar-tag">ciclo completo pela mesma pessoa</span>}
      </div>
      <div className="ar-grid">
        {papeis.map(p => (
          <div key={p.key} className={'ar-cell' + (p.pessoa ? (p.pendente ? ' pendente' : '') : ' vazio')}>
            <div className="ar-lb"><Icon name={p.icon} size={11} /> {p.label}</div>
            {p.pessoa ? (
              <>
                <div className="ar-nome">
                  {p.pessoa.tipo === 'agente' && <Icon name="cpu" size={11} style={{ color: 'var(--color-accent-deep)', flexShrink: 0 }} />}
                  <span>{p.pessoa.nome}</span>
                </div>
                <div className="ar-sub">
                  {p.pessoa.papel ? (ROLE_LABELS[p.pessoa.papel] || p.pessoa.papel) : (p.pessoa.tipo === 'agente' ? 'Agente de IA' : '—')}
                  {p.pessoa.previsto && ' · ainda não fechou'}
                  {p.nota && ' · ' + p.nota}
                </div>
              </>
            ) : (
              <div className="ar-vazio">{p.ausente}</div>
            )}
          </div>
        ))}
      </div>
      {at.handoff && (
        <div className="ar-foot">
          <Icon name="arrow-right" size={11} />
          Repassado por <strong>{at.handoff.nome}</strong>
          {at.handoff.meta.paraNome ? <> para <strong>{at.handoff.meta.paraNome}</strong></> : null}
        </div>
      )}
    </div>
  );
}

// ===== CadeiaTimeline component =====

function CadeiaTimeline({ cadeia }) {
  const lista = cadeia || [];
  if (!lista.length) {
    return (
      <div className="empty" style={{ padding: '32px 16px', textAlign: 'center' }}>
        <Icon name="link" size={28} style={{ color: 'var(--color-ink-faint)', marginBottom: 8 }} />
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Sem cadeia de atribuição</div>
        <div style={{ fontSize: 12, color: 'var(--color-ink-soft)', lineHeight: 1.5 }}>
          Os elos são criados automaticamente conforme o lead avança no pipeline.
        </div>
      </div>
    );
  }

  const fmtData = (iso) => {
    try {
      const d = new Date(iso);
      const p = n => String(n).padStart(2, '0');
      return p(d.getDate()) + '/' + p(d.getMonth()+1) + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
    } catch(e) { return String(iso || '—'); }
  };

  return (
    <div className="cadeia-timeline">
      {lista.map((elo, idx) => {
        const cfg = CADEIA_TIPO_CONFIG[elo.tipo] || { label: elo.tipo, icon: 'circle', color: 'cool' };
        const isLast = idx === lista.length - 1;
        return (
          <div key={elo.seq != null ? elo.seq : idx} className={'cadeia-item' + (isLast ? ' last' : '')}>
            <div className="cadeia-spine">
              <div className={'cadeia-icon tone-' + cfg.color}>
                <Icon name={cfg.icon} size={13} />
              </div>
              {!isLast && <div className="cadeia-line" />}
            </div>
            <div className="cadeia-body">
              <div className="cadeia-label">{cfg.label}</div>
              <div className="cadeia-ator">
                {elo.atorTipo === 'agente' && (
                  <Icon name="cpu" size={11} style={{ color: 'var(--color-accent-deep)', marginRight: 3, flexShrink: 0 }} />
                )}
                <span>{elo.atorNome}</span>
                {elo.papel && (
                  <span className="cadeia-papel">{ROLE_LABELS[elo.papel] || elo.papel.toUpperCase()}</span>
                )}
              </div>
              <div className="cadeia-data">{fmtData(elo.data)}</div>
              {elo.tipo === 'geracao' && elo.meta && elo.meta.scoreEnriquecimento != null && (
                <div className="cadeia-meta">
                  <span className="tag accent" style={{ fontSize: 10 }}>Score {elo.meta.scoreEnriquecimento}</span>
                  {elo.meta.fonte && <span style={{ fontSize: 11, color: 'var(--color-ink-soft)' }}> · {elo.meta.fonte}</span>}
                  {elo.meta.segmento && <span style={{ fontSize: 11, color: 'var(--color-ink-soft)' }}> · {elo.meta.segmento}</span>}
                </div>
              )}
              {elo.tipo === 'fechamento' && elo.meta && elo.meta.valorFinal > 0 && (
                <div className="cadeia-meta">
                  <span style={{ fontSize: 11, color: 'var(--color-ok)', fontWeight: 700 }}>{brl(elo.meta.valorFinal)}</span>
                  {elo.meta.proposta && <span style={{ fontSize: 11, color: 'var(--color-ink-soft)' }}> · {elo.meta.proposta}</span>}
                </div>
              )}
              {elo.tipo === 'handoff' && elo.meta && (
                <div className="cadeia-meta">
                  <span style={{ fontSize: 11, color: 'var(--color-ink-soft)' }}>
                    {elo.meta.de} → <strong>{elo.meta.para}</strong>
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export {
  ROLES, RBAC, useRbacVersion, PERM_DEPS, rbacSlug, PERM_LOCKED,
  SCOPE_DOMINIOS, SCOPE_OPCOES, RBAC_BUILTIN, ROLE_ORDER, PERMISSIONS,
  PERM_GROUPS, PERM_LABELS, DATA_SCOPE, ROLE_SCOPE, ROLE_LABELS,
  ROLE_DESC, ROLE_HOME, scopeOf, scopeLeads, scopeClientes,
  scopeAlerts, scopeChamados, useMoney, usePermission, usePermissions,
  useScope, useEscreveNoLead, useCargos, filterByScope, AtribuicaoResumo, CadeiaTimeline,
  RequirePermission, CADEIA_TIPO_CONFIG,
  soDoAdmin, travadaPara, permissoesAoAlternar, permissoesDoGrupo,
  diferencaDePermissoes, valoresDeFabrica, permissoesDoCargo,
};
